/**
 * CarouselManager - Gestionnaire de carousel avancé pour World of Numeric
 * Extrait de main.ts pour réutilisation dans BlogController
 */

export class CarouselManager {
  /**
   * Initialise un carousel avancé avec navigation, tactile, accessibilité
   */
  static initializeCarousel(container: HTMLElement): void {
    if (!container) {
      console.error('❌ Container carousel manquant');
      return;
    }

    const carousel = container.querySelector('.horizontal-articles-container') as HTMLElement;
    const prevButton = container.querySelector('.carousel-prev') as HTMLButtonElement;
    const nextButton = container.querySelector('.carousel-next') as HTMLButtonElement;
    const dots = container.querySelectorAll('.carousel-pagination .dot');
    
    if (!carousel || !prevButton || !nextButton) {
      console.error('❌ Éléments carousel manquants');
      return;
    }

    // Variables pour le défilement et les gestures
    let isDragging = false;
    let startPosition = 0;
    let scrollLeft = 0;
    let currentIndex = 0;
    let totalArticles = carousel.children.length;
    
    console.log(`🎠 Initialisation carousel avec ${totalArticles} articles`);

    // === CARDS ADAPTATIVES ===
    function getVisibleCards(): number {
      const containerWidth = carousel.offsetWidth;
      const cardWidth = 290 + 20; // largeur carte + gap
      return Math.max(1, Math.floor(containerWidth / cardWidth));
    }

    function getCardWidth(): number {
      return carousel.querySelector('.article-card')?.clientWidth || 290;
    }

    // === ACCESSIBILITÉ RENFORCÉE ===
    function updateAccessibility() {
      const visibleCards = getVisibleCards();
      const maxIndex = Math.max(0, totalArticles - visibleCards);
      
      prevButton.setAttribute('aria-label', `Article précédent (${currentIndex + 1}/${totalArticles})`);
      nextButton.setAttribute('aria-label', `Article suivant (${Math.min(currentIndex + visibleCards, totalArticles)}/${totalArticles})`);
      
      // Désactiver les boutons si nécessaire
      prevButton.disabled = currentIndex === 0;
      nextButton.disabled = currentIndex >= maxIndex;
      
      // Styles pour boutons désactivés
      prevButton.style.opacity = prevButton.disabled ? '0.5' : '1';
      nextButton.style.opacity = nextButton.disabled ? '0.5' : '1';
      prevButton.style.cursor = prevButton.disabled ? 'not-allowed' : 'pointer';
      nextButton.style.cursor = nextButton.disabled ? 'not-allowed' : 'pointer';
      
      // ARIA pour le carousel
      carousel.setAttribute('role', 'region');
      carousel.setAttribute('aria-label', 'Carousel d\'articles');
      carousel.setAttribute('aria-live', 'polite');
    }

    // === NAVIGATION AMÉLIORÉE ===
    function scrollToIndex(index: number) {
      const cardWidth = getCardWidth();
      const scrollDistance = index * (cardWidth + 20);
      carousel.scrollTo({
        left: scrollDistance,
        behavior: 'smooth'
      });
      currentIndex = index;
      updateAccessibility();
      updatePagination();
    }

    // Navigation par boutons avec cards adaptatives
    prevButton.addEventListener('click', () => {
      if (currentIndex > 0) {
        const visibleCards = getVisibleCards();
        const newIndex = Math.max(0, currentIndex - visibleCards);
        scrollToIndex(newIndex);
      }
    });

    nextButton.addEventListener('click', () => {
      const visibleCards = getVisibleCards();
      const maxIndex = Math.max(0, totalArticles - visibleCards);
      if (currentIndex < maxIndex) {
        const newIndex = Math.min(maxIndex, currentIndex + visibleCards);
        scrollToIndex(newIndex);
      }
    });

    // === NAVIGATION AU CLAVIER ===
    function handleKeyboardNavigation(e: KeyboardEvent) {
      // Vérifier si le carousel ou un de ses enfants a le focus
      if (!container.contains(document.activeElement)) return;
      
      switch(e.key) {
        case 'ArrowLeft':
          e.preventDefault();
          if (!prevButton.disabled) prevButton.click();
          break;
        case 'ArrowRight':
          e.preventDefault();
          if (!nextButton.disabled) nextButton.click();
          break;
        case 'Home':
          e.preventDefault();
          scrollToIndex(0);
          break;
        case 'End':
          e.preventDefault();
          const maxIndex = Math.max(0, totalArticles - getVisibleCards());
          scrollToIndex(maxIndex);
          break;
      }
    }

    // Ajouter la navigation clavier
    document.addEventListener('keydown', handleKeyboardNavigation);

    // Rendre le carousel focusable
    carousel.setAttribute('tabindex', '0');
    carousel.addEventListener('focus', () => {
      carousel.style.outline = '2px solid #3498db';
    });
    carousel.addEventListener('blur', () => {
      carousel.style.outline = 'none';
    });

    // === GESTURES TACTILES AMÉLIORÉS ===
    let startX = 0, startY = 0, distX = 0, distY = 0;
    let isScrolling = false;

    function handleTouchStart(e: TouchEvent) {
      const touch = e.touches[0];
      startX = touch.clientX;
      startY = touch.clientY;
      isScrolling = false;
      startPosition = startX;
      scrollLeft = carousel.scrollLeft;
    }

    function handleTouchMove(e: TouchEvent) {
      if (!startX || !startY) return;
      
      const touch = e.touches[0];
      distX = touch.clientX - startX;
      distY = touch.clientY - startY;
      
      // Déterminer la direction du scroll
      if (!isScrolling) {
        isScrolling = Math.abs(distX) > Math.abs(distY);
      }
      
      // Si c'est un scroll horizontal, empêcher le scroll vertical
      if (isScrolling) {
        e.preventDefault();
        carousel.scrollLeft = scrollLeft - distX;
      }
    }

    function handleTouchEnd(e: TouchEvent) {
      if (!isScrolling) return;
      
      const cardWidth = getCardWidth() + 20; // largeur + gap
      const threshold = cardWidth * 0.3; // 30% d'une carte
      
      if (Math.abs(distX) > threshold) {
        if (distX > 0) {
          // Swipe vers la droite (précédent)
          if (!prevButton.disabled) prevButton.click();
        } else {
          // Swipe vers la gauche (suivant)  
          if (!nextButton.disabled) nextButton.click();
        }
      } else {
        // Retour à la position d'origine si le swipe n'est pas assez fort
        carousel.scrollTo({
          left: scrollLeft,
          behavior: 'smooth'
        });
      }
      
      // Reset
      startX = startY = distX = distY = 0;
      isScrolling = false;
    }

    // Event listeners pour les touches
    carousel.addEventListener('touchstart', handleTouchStart, { passive: false });
    carousel.addEventListener('touchmove', handleTouchMove, { passive: false });
    carousel.addEventListener('touchend', handleTouchEnd);

    // Défilement souris amélioré
    carousel.addEventListener('mousedown', (e) => {
      isDragging = true;
      startPosition = e.pageX - carousel.offsetLeft;
      scrollLeft = carousel.scrollLeft;
      carousel.style.cursor = 'grabbing';
      carousel.style.userSelect = 'none';
    });

    carousel.addEventListener('mouseleave', () => {
      isDragging = false;
      carousel.style.cursor = 'grab';
      carousel.style.userSelect = 'auto';
    });

    carousel.addEventListener('mouseup', () => {
      isDragging = false;
      carousel.style.cursor = 'grab';
      carousel.style.userSelect = 'auto';
      updatePagination();
    });

    carousel.addEventListener('mousemove', (e) => {
      if (!isDragging) return;
      e.preventDefault();
      const x = e.pageX - carousel.offsetLeft;
      const walk = (x - startPosition) * 1.5; // Multiplicateur de vitesse ajusté
      carousel.scrollLeft = scrollLeft - walk;
    });

    // === PAGINATION DYNAMIQUE ===
    function updatePagination() {
      if (!dots.length) return;
      
      const scrollPosition = carousel.scrollLeft;
      const maxScroll = carousel.scrollWidth - carousel.clientWidth;
      const scrollPercentage = maxScroll > 0 ? scrollPosition / maxScroll : 0;
      
      // Calculer l'index actuel basé sur la position
      const cardWidth = getCardWidth() + 20;
      const newIndex = Math.round(scrollPosition / cardWidth);
      currentIndex = Math.max(0, Math.min(newIndex, totalArticles - 1));
      
      // Diviser le carousel en sections selon le nombre de points
      const activeDotIndex = Math.min(
        Math.floor(scrollPercentage * dots.length),
        dots.length - 1
      );
      
      dots.forEach((dot, index) => {
        dot.classList.toggle('active', index === activeDotIndex);
        // Accessibilité pour les dots
        (dot as HTMLElement).setAttribute('aria-label', `Aller à la section ${index + 1}`);
        (dot as HTMLElement).setAttribute('role', 'button');
        (dot as HTMLElement).setAttribute('tabindex', '0');
      });
      
      updateAccessibility();
    }

    // Cliquer sur les points de pagination
    dots.forEach((dot, index) => {
      dot.addEventListener('click', () => {
        const totalSections = dots.length;
        const articlesPerSection = Math.ceil(totalArticles / totalSections);
        const targetIndex = index * articlesPerSection;
        scrollToIndex(Math.min(targetIndex, totalArticles - getVisibleCards()));
      });
    });

    // === RESPONSIVE ===
    let resizeTimeout: number;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = window.setTimeout(() => {
        updateAccessibility();
        updatePagination();
      }, 250);
    });

    // === INITIALISATION ===
    updateAccessibility();
    updatePagination();
    
    console.log('🎠 ✅ Carousel avancé initialisé avec succès');
  }
}

export default CarouselManager;
